import { AssetIcon } from 'assets/icons/AssetIcon';
import useConditionalTimeout from 'beautiful-react-hooks/useConditionalTimeout';
import cx from 'classnames';
import Button from 'components/button/Button';
import Input from 'components/input/Input';
import PageActions from 'components/page-actions/PageActions';
import Select from 'components/select/Select';
import TimerText from 'components/timer-text/TimerText';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { BE_DEFINES } from 'constants/backend-defines.const';
import { NUMER_WARNING_OUT_OF_PAPER } from 'constants/common.const';
import { BROADCAST_EVENTS } from 'constants/dom-element.const';
import { I18nNamespace } from 'constants/i18n.const';
import {
  CLOSE_AFTER_TIME_DURATION,
  PAYMENT_DEPOSIT_DEBOUNCE_TIME,
} from 'constants/photo.const';
import { useAppContext } from 'context/AppContext';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { emitSeqLog } from 'functions/seq-logging.func';
import { formatMoneyVN } from 'helpers/math.helper';
import { isEqualVal } from 'helpers/string.helper';
import { checkTimeIsInRange, formatDate } from 'helpers/time.helper';
import { useModal } from 'hooks/useModal';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { useOptions } from 'hooks/useOptions';
import { usePageTimer } from 'hooks/usePageTimer';
import { map } from 'lodash';
import { SocketEventDataModel } from 'models/photo/socket.model';
import { PaymentMethodEnum } from 'models/photo/transaction.model';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useCloseBillAcceptorApiActionMutation,
  useOpenBillAcceptorApiActionMutation,
} from 'store/api/payment.slice-api';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useDebouncedCallback } from 'use-debounce';
import KeyboardDrawer from './KeyboardDrawer';
import './payment.css';
import PinCodePopupContent from './PinCodePopupContent';
import { useCheckPayment } from './useCheckPayment';

function Payment() {
  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getPrevPath } = useNewPhotoLifeCycleStep();
  const [openBillAcceptorApiAction] = useOpenBillAcceptorApiActionMutation();
  const [closeBillAcceptorApiAction] = useCloseBillAcceptorApiActionMutation();
  const {
    isEnoughDeposit,
    costOfPayment,
    handleBeforeNextWarningAboutMoney,
    handleCheckBeforeCreateQRPayment,
  } = useCheckPayment();
  const { menuTestModeOptions } = useOptions();
  const { audio } = useSoundContext();
  const { isOnline } = useAppContext();
  const { currentLayout } = useBoothAppContext();
  const { openConfirmModal, closeConfirmModal } = useModal();
  const { second, resetTime } = usePageTimer({
    isBeforeTransaction: true,
  });
  // state
  const {
    quantitySheet,
    transactionId,
    depositAmount,
    appliedVoucherCode,
    appliedVoucherValue,
    isChooseQRCodeToPay,
    isMomoTransactionLoading,
    paymentMethodForTest,
  } = useAppSelector((state) => state.selfeApp);
  const { isVerticalScreen, boothInfo, TestedBooth, numberOfRemain } =
    useAppSelector((s) => s.booth);

  const [keyboardOpen, setKeyboardOpen] = useState<boolean>(false);

  const handleMapMoney = () => {
    const rawDenominations = boothInfo?.CurrencyDescription || '';
    const denominationArray = rawDenominations
      .split(',')
      .map((x) => Number(x.trim()))
      .filter((x) => !isNaN(x));

    const moneyList = denominationArray
      .map((x) => `${x}${boothInfo?.CurrencySymbol}`)
      .join(', ');

    return moneyList;
  };

  const handleChangeAmountMorePic = (quantity: number) => () =>
    dispatch(setSelfeAppStateAction({ quantitySheet: quantity }));

  // click Back
  const handleBack = () => {
    audio?.playBackClick?.();
    navigate(getPrevPath() || '');
  };

  // voucher
  const handleShowTestBoothKeyboard = useCallback(
    (testType: number) => {
      if (!boothInfo) return;

      const { FromTime, ToTime, NumberOfUses } = boothInfo;
      const testedNumber =
        formatDate(TestedBooth?.date) !== formatDate(moment())
          ? 0
          : TestedBooth?.numberOfUses || 0;

      // Validate time range for TESTPC
      if (testType === PaymentMethodEnum.TESTPC) {
        if (!checkTimeIsInRange(FromTime, ToTime)) {
          return openConfirmModal({
            content: t('common:warningOutOfTimeToTestPC'),
            hideNext: true,
          });
        }
        // Check usage limit
        if (testedNumber >= NumberOfUses) {
          return openConfirmModal({
            content: t('common:warningOutOfTurnToTestPC'),
            hideNext: true,
          });
        }
      }

      // Show pin code popup
      openConfirmModal({
        RenderContent: () => (
          <PinCodePopupContent
            paymentMethod={testType}
            onClose={closeConfirmModal}
          />
        ),
        hidePrevNext: true,
        onClose: closeConfirmModal,
      });
    },
    [boothInfo, closeConfirmModal, openConfirmModal, t, TestedBooth],
  );

  // voucher
  const handleShowKeyboard = useCallback(
    (isClose: boolean = false) =>
      () =>
        setKeyboardOpen(!isClose),
    [],
  );

  const handleApplyVoucher = useCallback(
    (voucher: string, voucherValue: number) => {
      const newDepositAmount = depositAmount + voucherValue;
      dispatch(
        setSelfeAppStateAction({
          depositAmount: newDepositAmount,
          appliedVoucherCode: voucher,
          appliedVoucherValue: voucherValue,
        }),
      );
    },
    [depositAmount, dispatch],
  );

  const handleDeposit: any = useDebouncedCallback(
    useCallback(
      async (e: CustomEvent<SocketEventDataModel>) => {
        emitSeqLog?.({
          messageTemplate: `[Socket] Listening Deposit Type: typeHub=${
            e?.detail?.typeHub
          } | Amount hiện tại=${depositAmount} | nạp=${+e?.detail?.data || 0}`,
        });
        if (isEqualVal(e?.detail?.typeHub, BE_DEFINES.HUB_TYPE.BILL_ACCEPTOR)) {
          dispatch(
            setSelfeAppStateAction({
              depositAmount: depositAmount + (+e?.detail?.data || 0),
            }),
          );
          audio?.playDeposit?.();
          resetTime();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dispatch, resetTime, depositAmount],
    ),
    PAYMENT_DEPOSIT_DEBOUNCE_TIME,
    { trailing: false, leading: true },
  );

  // open bill acceptor session
  useEffect(() => {
    if (
      transactionId &&
      !isChooseQRCodeToPay &&
      depositAmount < costOfPayment
    ) {
      openBillAcceptorApiAction({ transactionId });
    }
  }, [
    dispatch,
    openBillAcceptorApiAction,
    isChooseQRCodeToPay,
    transactionId,
    depositAmount,
    costOfPayment,
    boothInfo?.numberOfPrintRemain,
  ]);

  // useEffect(() => {
  //   if (boothInfo?.numberOfPrintRemain! <= quantitySheet) {
  //     openConfirmModal({
  //       RenderContent: () => (
  //         <NumberRemainPopup content={t('common:notEnoughPaper')} />
  //       ),
  //       hideNext: true,
  //     });
  //   }
  // }, [boothInfo?.numberOfPrintRemain, quantitySheet, t]);

  // close bill acceptor session
  useConditionalTimeout(
    () => closeBillAcceptorApiAction(),
    CLOSE_AFTER_TIME_DURATION,
    depositAmount >= costOfPayment,
  );

  // Add event broadcast bill acceptor
  React.useEffect(() => {
    document.addEventListener(BROADCAST_EVENTS.BILL_ACCEPTOR, handleDeposit);
    return () => {
      document.removeEventListener(
        BROADCAST_EVENTS.BILL_ACCEPTOR,
        handleDeposit,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderPaymentActions = () => (
    <PageActions
      NextButtonProps={{
        children:
          isChooseQRCodeToPay && !isEnoughDeposit
            ? t('common:qrCode')
            : t('common:continue'),
        disabled: !isChooseQRCodeToPay && (!isEnoughDeposit || !isOnline),
        loading: isMomoTransactionLoading,
      }}
      handleBack={handleBack}
      handleContinue={
        isChooseQRCodeToPay
          ? handleCheckBeforeCreateQRPayment
          : handleBeforeNextWarningAboutMoney
      }
      className={cx({ 'payment__page-actions--vertical': isVerticalScreen })}
    />
  );

  return (
    <>
      <div className="payment">
        <div className="absolute top-0 right-0 flex items-center">
          <TimerText second={second} />
          <Select
            surfixIcon={<AssetIcon.MenuIcon />}
            SurfixIconProps={{ className: 'pb-icon-btn' }}
            value={paymentMethodForTest}
            options={
              boothInfo?.IsTestFrame
                ? menuTestModeOptions
                : menuTestModeOptions.filter(
                    (item) => item.value !== PaymentMethodEnum.TEST_FRAME,
                  )
            }
            className="!bg-transparent !p-0 payment__test-booth-mode-select"
            onChange={(val) => handleShowTestBoothKeyboard(val as number)}
            OuterProps={{
              className: 'payment__test-booth-mode-select--wrapper',
            }}
          />
        </div>
        {isVerticalScreen && renderPaymentActions()}
        <div className="payment__form">
          <div className="payment__form-row">
            <div className="payment__field-label">
              <Typography variant={TYPOGRAPHY_VARIANTS.H2} firstCapCase>
                {t('page:printAmount')}
              </Typography>
            </div>
            <div className="payment-right-group">
              <div className="button-group-more-pic">
                {map(currentLayout?.prices, (item) => (
                  <Button
                    key={item?.numberOfPicture}
                    className="pb-square-button"
                    variantFill={isEqualVal(
                      item?.numberOfPicture,
                      quantitySheet,
                    )}
                    onClick={handleChangeAmountMorePic(item?.numberOfPicture)}
                  >
                    {item?.numberOfPicture}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          {/* <div className="payment__form-row change-money-warning tip-multiple-theme">
            <div className="payment__field-label" />
            <div className="payment-right-group">
              <Typography
                className="change-money-warning-text"
                variant={TYPOGRAPHY_VARIANTS.SMALL}
                firstCapCase
              >
                {t('common:tipSelectMultipelTheme')}
              </Typography>
            </div>
          </div> */}
          <div className="payment__form-row change-money-warning tip-multiple-theme">
            <div className="payment__field-label" />
            {numberOfRemain <= NUMER_WARNING_OUT_OF_PAPER && (
              <div className="payment-right-group">
                <Typography
                  className="number-of-remain-text"
                  variant={TYPOGRAPHY_VARIANTS.SMALL}
                  firstCapCase
                >
                  {t('common:numberOfPrintRemain')}: {numberOfRemain}
                </Typography>
              </div>
            )}
          </div>
          <div className="payment__form-row">
            <div className="payment__field-label">
              <Typography variant={TYPOGRAPHY_VARIANTS.H2} firstCapCase>
                {t('common:voucherCode')}
              </Typography>
            </div>
            <div className="payment-right-group">
              <Input
                className="input-voucher"
                value={
                  appliedVoucherCode
                    ? `${appliedVoucherCode} +${formatMoneyVN(
                        appliedVoucherValue,
                      )} ${boothInfo?.CurrencySymbol}`
                    : ''
                }
                placeholder={`${t('common:voucherCodePlaceholder')}`}
                readOnly
                disabled={!isOnline || !!appliedVoucherCode}
                onClick={handleShowKeyboard(!isOnline)}
              />
            </div>
          </div>
          <div className="payment__form-row">
            <div className="payment__field-label">
              <Typography variant={TYPOGRAPHY_VARIANTS.H2} firstCapCase>
                {t('page:needPayment')}
              </Typography>
            </div>
            <div className="payment-right-group">
              <Input
                className="input-need-pay"
                value={`${formatMoneyVN(costOfPayment)} ${
                  boothInfo?.CurrencySymbol || 'đ'
                }`}
                readOnly
              />
            </div>
          </div>
          <div className="payment__form-row">
            <div className="payment__field-label">
              <Typography variant={TYPOGRAPHY_VARIANTS.H2} firstCapCase>
                {t('page:deposited')}
              </Typography>
            </div>
            <div className="payment-right-group payment-right-group-deposited">
              <Input
                className="input-deposited"
                value={`${formatMoneyVN(depositAmount || 0)} ${
                  boothInfo?.CurrencySymbol || 'đ'
                }`}
                readOnly
                variantFill
              />
            </div>
          </div>
          <div className="payment__form-row change-money-warning">
            <div className="payment__field-label" />
            <div className="payment-right-group">
              <Typography
                className="change-money-warning-text"
                variant={TYPOGRAPHY_VARIANTS.SMALL}
                firstCapCase
              >
                {depositAmount > costOfPayment
                  ? t('page:changeMoneyWarning')
                  : t('page:onlyAcceptSpecifyMoney', {
                      moneyList: handleMapMoney(),
                    })}
              </Typography>
              <Typography
                className="change-money-warning-text"
                variant={TYPOGRAPHY_VARIANTS.SMALL}
                upperCase
              >
                {t('page:straightenMoneyAndPutToMachine')}
              </Typography>
            </div>
          </div>
        </div>
        <div />
      </div>
      {!isVerticalScreen && renderPaymentActions()}
      <KeyboardDrawer
        open={keyboardOpen}
        onClose={handleShowKeyboard(true)}
        onOk={handleApplyVoucher}
      />
    </>
  );
}

export default Payment;
